import React, { useEffect, useMemo } from 'react';
// 1. Importamos o createPortal do react-dom
import { createPortal } from 'react-dom';
import { X, ChefHat } from 'lucide-react';

function formatPrice(price) {
    if (price === null || price === undefined) return "R$ --";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
}

function formatCategoryName(name) {
    if (name === 'all') return 'Todos';
    return name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function DishDetailModal({ dish, onClose }) {
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => { 
            document.body.style.overflow = 'auto'; 
            document.removeEventListener('keydown', handleKeyDown); 
        };
    }, [onClose]);

    const { nome, imagem, preco, descricao_completa, ingredientes, categorias, esgotado } = dish;

    const ingredientText = useMemo(() => {
        if (ingredientes && typeof ingredientes === 'object') {
            return Object.keys(ingredientes).join(' • '); 
        }
        return null;
    }, [ingredientes]);

    // 2. Envolvemos o JSX do modal no createPortal, enviando-o para o document.body
    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm md:p-6 animate-in fade-in duration-300" onClick={onClose}>
            
            <div 
                className="bg-[#FFF5EE] w-full md:max-w-2xl h-[90vh] md:h-auto md:max-h-[90vh] rounded-t-3xl md:rounded-3xl flex flex-col relative overflow-hidden animate-in slide-in-from-bottom-full md:zoom-in-95 duration-300 shadow-2xl"
                onClick={e => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-colors shadow-sm border border-white/20">
                    <X size={20} strokeWidth={2.5} />
                </button>
                
                <div className="w-full h-64 md:h-80 shrink-0 relative bg-stone-200">
                    <img src={imagem || '/images/placeholder.webp'} alt={nome} className="w-full h-full object-cover" />
                    {esgotado && (
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
                            <span className="bg-red-600 text-white text-base font-bold uppercase tracking-widest px-6 py-2 rounded-full shadow-lg border border-white/20">Esgotado</span>
                        </div>
                    )}
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 md:p-8">
                    <div className="flex justify-between items-start gap-4 mb-6">
                        <h2 className="font-serif text-3xl md:text-4xl text-[#528a89] font-bold leading-tight flex-1">{nome}</h2>
                        <span className="font-serif text-2xl md:text-3xl text-[#971253] font-bold shrink-0">{formatPrice(preco)}</span>
                    </div>
                    
                    <p className="text-stone-600 text-lg leading-relaxed mb-8 whitespace-pre-line font-light">
                        {descricao_completa}
                    </p>
                    
                    {ingredientText && (
                        <div className="bg-white p-5 rounded-2xl border border-[#dbcdac]/40 mb-8 shadow-sm">
                            <h3 className="flex items-center gap-2 font-serif text-lg text-[#971253] mb-3">
                                <ChefHat size={20} /> O que vai neste prato?
                            </h3>
                            <p className="text-stone-600 leading-relaxed font-medium">
                                {ingredientText}
                            </p>
                        </div>
                    )}
                    
                    {categorias && categorias.length > 0 && (
                        <div>
                            <h3 className="text-xs font-bold uppercase tracking-widest text-[#dbcdac] mb-3">Categorias</h3>
                            <div className="flex flex-wrap gap-2">
                                {categorias.map((tag) => (
                                    <span key={tag} className="bg-[#528a89]/10 border border-[#528a89]/20 text-[#528a89] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                        {formatCategoryName(tag)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body // <-- Destino final do teletransporte do React
    );
}

export default DishDetailModal;